import type { Column } from 'exceljs';
import { farmToFarmColumns } from '@/util/farmToFarmColumns';
import { FarmToFarmReport } from '@/types';

/**
 * Writes the farm-to-farm table to a workbook, one worksheet per period.
 *
 * All periods are already in the fetched report, so both export options are pure client-side
 * and neither needs another request.
 *
 * Returns the blob rather than downloading it: naming and saving the file are the view's
 * decisions, and keeping the DOM out of here leaves the workbook itself checkable on its own.
 *
 * Two things this deliberately does NOT do:
 *
 *  - It does not write pre-formatted strings. Cells are real numbers with a number format, so
 *    the recipient can sort, pivot and chart them. A sheet of text that merely looks like
 *    numbers is useless for the benchmarking this report exists to support.
 *  - It does not drop the estimated marking. An exported spreadsheet outlives the screen it
 *    came from, so an estimated figure carries italics and a cell note there too - otherwise
 *    the export quietly launders guesses into firm numbers.
 */

const LABEL_COLUMN_WIDTH = 42;
const UNIT_COLUMN_WIDTH = 10;
const VALUE_COLUMN_WIDTH = 16;

function numberFormatFor(decimals: number): string {
    return decimals > 0 ? `#,##0.${'0'.repeat(decimals)}` : '#,##0';
}

/**
 * Excel forbids several characters in sheet names and caps them at 31 characters; the label
 * arrives already localised, so neither can be assumed away. ExcelJS throws on a name that breaks
 * either rule, which would fail the whole export rather than one sheet.
 */
const MAX_SHEET_NAME_LENGTH = 31;

function sheetName(label: string): string {
    return label.replace(/[\\/*?:[\]]/g, '-').slice(0, MAX_SHEET_NAME_LENGTH);
}

export interface FarmToFarmWorkbookOptions {
    report: FarmToFarmReport;
    /** Periods to write, in order. A subset of report.periods. */
    periods: string[];
    /** Column header for the rollup column, e.g. the directory name. */
    aggregateLabel: string;
    /** Localised header for the label and unit columns. */
    labelHeader: string;
    unitHeader: string;
    /** Renders a period key as the sheet name a reader sees. Supplied so this stays free of i18n. */
    periodLabel: (period: string) => string;
}

export async function buildFarmToFarmWorkbook(options: FarmToFarmWorkbookOptions): Promise<Blob> {
    const ExcelJS = await import('exceljs');
    const { report, periods, aggregateLabel } = options;

    const workbook = new ExcelJS.Workbook();
    workbook.created = new Date();

    periods.forEach((period) => {
        const sheet = workbook.addWorksheet(sheetName(options.periodLabel(period)));

        // Rebuilt per period: the key and header are fixed, but each column's state and note are
        // the ones for this period.
        const columns = farmToFarmColumns(report, period, aggregateLabel);

        const columnWidths: Partial<Column>[] = [{ width: LABEL_COLUMN_WIDTH }, { width: UNIT_COLUMN_WIDTH }, ...columns.map(() => ({ width: VALUE_COLUMN_WIDTH }))];
        sheet.columns = columnWidths as Column[];

        const header = sheet.addRow([options.labelHeader, options.unitHeader, ...columns.map((column) => column.name)]);
        header.font = { bold: true };

        report.groups.forEach((group) => {
            const groupRow = sheet.addRow([group.header]);
            groupRow.font = { bold: true };

            group.rows.forEach((row) => {
                const excelRow = sheet.addRow([row.label, row.unit, ...columns.map((column) => column.valueFor(row))]);

                columns.forEach((column, index) => {
                    // +3: the label and unit columns come first, and ExcelJS is 1-based.
                    const cell = excelRow.getCell(index + 3);
                    cell.numFmt = numberFormatFor(row.decimals);

                    if (column.state === 'estimated') {
                        cell.font = { italic: true };
                    }

                    if (column.note && (column.state === 'estimated' || cell.value === null)) {
                        cell.note = column.note;
                    }
                });
            });
        });

        // Freeze the label and unit columns and the header row, so a wide comparison stays
        // readable while scrolling - the same reason the on-screen table pins them.
        sheet.views = [{ state: 'frozen', xSplit: 2, ySplit: 1 }];
    });

    // ExcelJS declares its own Buffer type rather than reusing ArrayBuffer, so the cast is
    // needed to hand it to Blob.
    const buffer = (await workbook.xlsx.writeBuffer()) as ArrayBuffer;

    return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}
